/**
 * Flow Definitions for Claim Portal Navigation
 *
 * Defines the sequential flow of screens for each claim type:
 * - MEDICAL: Medical expense claims
 * - CANCELLATION: Trip cancellation/interruption claims
 * - BAGGAGE: Lost/damaged baggage claims
 *
 * Each flow contains an ordered array of screen codes that define
 * the navigation path through the claim submission process.
 *
 * Some screens are conditional and may be skipped based on user selections.
 */

export type FlowType = 'MEDICAL' | 'CANCELLATION' | 'BAGGAGE' | 'APPEND_EXPENSES' | 'APPEND_DOCUMENTS';

/**
 * Medical Flow
 * For medical emergency and medical expense claims
 */
export const MEDICAL_FLOW: string[] = [
  '00-02-AVIS',              // Automated consent
  '00-03-EVENEMENT',         // Event date (unconditional — every tenant; not a progress step, ADR-061)
  '01-00-DEMANDEUR',         // Contact preferences
  '01-01-CONTRAT',           // Contract selection
  '01-02-AUTRES-CONTRATS',   // Other insurance
  '01-03-PAIEMENT-VOYAGE',   // Trip payment method (conditional: skipped if trip paid with the declared card)
  '02-00-GARANTIE',          // Claim type selection (branches here)
  '02M-01-BENEFICIAIRE',     // Beneficiary (Medical)
  '02M-02-VOYAGE',           // Travel confirmation (Medical)
  '03M-00-ACCIDENT',         // Specific verifications
  '03M-01-DETAILS',          // Event details
  '03M-02-VERIFICATIONS',    // Medical verification
  '04M-00-REMBOURSEMENT',    // Reimbursement details
  '04-01-FACTURES',          // Invoice upload
  '04-02-VALIDATION',        // Expense validation
  '04-03-JUSTIFICATIFS',     // Supporting documents
  '05-01-PAIEMENT',          // Banking info
  '05M-02-MANDAT',           // Mandate signature
  '05-03-FIN'                // Confirmation
];

/**
 * Cancellation Flow
 * For trip cancellation and interruption claims
 *
 * Note: Steps 03A-01-DETAILS1 through 03A-01-DETAILS5 are mutually exclusive.
 * The specific DETAILS screen is determined by the cancellation reason selected.
 */
export const CANCELLATION_FLOW: string[] = [
  '00-02-AVIS',              // Automated consent
  '00-03-EVENEMENT',         // Event date (unconditional — every tenant; not a progress step, ADR-061)
  '01-00-DEMANDEUR',         // Contact preferences
  '01-01-CONTRAT',           // Contract selection
  '01-02-AUTRES-CONTRATS',   // Other insurance
  '01-03-PAIEMENT-VOYAGE',   // Trip payment method (conditional: skipped if trip paid with the declared card)
  '02-00-GARANTIE',          // Claim type selection (branches here)
  '02A-01-BENEFICIAIRE',     // Beneficiary (Cancellation)
  '02A-02-VOYAGE',           // Travel cancellation details
  // Next screen determined by cancellation type:
  // '03A-01-DETAILS1' or '03A-01-DETAILS2' or '03A-01-DETAILS3' or '03A-01-DETAILS4' or '03A-01-DETAILS5'
  '03A-02-VERIFICATIONS',    // Medical verification (conditional: only if medical reason)
  '04A-00-CATEGORIE',        // Cancellation category
  '04-01-FACTURES',          // Invoice upload
  '04-02-VALIDATION',        // Expense validation
  '04-03-JUSTIFICATIFS',     // Supporting documents
  '05-01-PAIEMENT',          // Banking info
  '05-03-FIN'                // Confirmation
];

/**
 * Cancellation Details Screens (mutually exclusive)
 * Only one of these will be shown based on cancellation reason
 */
export const CANCELLATION_DETAILS_SCREENS = [
  '03A-01-DETAILS1',  // Death-related cancellation
  '03A-01-DETAILS2',  // Medical-related cancellation
  '03A-01-DETAILS3',  // Professional-related cancellation
  '03A-01-DETAILS4',  // Transportation-related cancellation
  '03A-01-DETAILS5'   // Other reasons
];

/**
 * Baggage Flow
 * For lost, stolen, or damaged baggage claims
 */
export const BAGGAGE_FLOW: string[] = [
  '00-02-AVIS',              // Automated consent
  '00-03-EVENEMENT',         // Event date (unconditional — every tenant; not a progress step, ADR-061)
  '01-00-DEMANDEUR',         // Contact preferences
  '01-01-CONTRAT',           // Contract selection
  '01-02-AUTRES-CONTRATS',   // Other insurance
  '01-03-PAIEMENT-VOYAGE',   // Trip payment method (conditional: skipped if trip paid with the declared card)
  '02-00-GARANTIE',          // Claim type selection (branches here)
  '02B-01-BENEFICIAIRE',     // Beneficiary (Baggage)
  '02B-02-VOYAGE',           // Travel confirmation (Baggage)
  '03B-01-DETAILS',          // Baggage incident details
  '03B-02-VERIFICATIONS',    // Baggage verifications
  '04B-00-REMBOURSEMENT',    // Baggage reimbursement category
  '04-01-FACTURES',          // Invoice upload
  '04-02-VALIDATION',        // Expense validation
  '04-03-JUSTIFICATIFS',     // Supporting documents
  '05-01-PAIEMENT',          // Banking info
  '05-03-FIN'                // Confirmation
];

/**
 * Common Screens (shared across all flows)
 * These screens appear in all claim flows
 */
export const COMMON_SCREENS = [
  '00-00-ACCUEIL',           // Home screen
  '00-01-NOUVELLE',          // Prepare claim
  '00-02-AVIS',              // Automated consent
  '00-03-EVENEMENT',         // Event date (unconditional — every tenant; not a progress step, ADR-061)
  '01-00-DEMANDEUR',         // Contact preferences
  '01-01-CONTRAT',           // Contract selection
  '01-02-AUTRES-CONTRATS',   // Other insurance
  '01-03-PAIEMENT-VOYAGE',   // Trip payment method (conditional)
  '02-00-GARANTIE',          // Claim type selection
  '04-01-FACTURES',          // Invoice upload
  '04-02-VALIDATION',        // Expense validation
  '04-03-JUSTIFICATIFS',     // Supporting documents
  '05-00-ELIGIBILITE',       // Eligibility analysis
  '05-01-PAIEMENT',          // Banking info
  '05-03-FIN'                // Confirmation
];

/**
 * Conditional Screens
 * These screens may be skipped based on user selections or data
 */
export const CONDITIONAL_SCREENS = {
  '05M-02-MANDAT': {
    flows: ['MEDICAL'],
    condition: 'reimbursementToThirdParty',
    description: 'Only shown if reimbursement is to a third party (e.g., hospital)'
  },
  '03A-02-VERIFICATIONS': {
    flows: ['CANCELLATION'],
    condition: 'medicalReason',
    description: 'Only shown if cancellation reason is medical'
  },
  ...CANCELLATION_DETAILS_SCREENS.reduce((acc, screen) => {
    acc[screen] = {
      flows: ['CANCELLATION'],
      condition: 'cancellationReasonType',
      description: 'Shown based on selected cancellation reason'
    };
    return acc;
  }, {} as Record<string, { flows: string[], condition: string, description: string }>)
};

/**
 * Get the flow definition for a specific flow type
 */
export const getFlowByType = (flowType: FlowType): string[] => {
  switch (flowType) {
    case 'MEDICAL':
      return MEDICAL_FLOW;
    case 'CANCELLATION':
      return CANCELLATION_FLOW;
    case 'BAGGAGE':
      return BAGGAGE_FLOW;
    case 'APPEND_EXPENSES':
      return APPEND_EXPENSES_FLOW;
    case 'APPEND_DOCUMENTS':
      return APPEND_DOCUMENTS_FLOW;
    default:
      return [];
  }
};

/**
 * Append Expenses Flow (DEV-6623)
 * Adds new expenses to an already-submitted medical claim without creating a NOVA duplicate.
 * Starts from the 00-10-RECLAMATIONS detail page (via a dedicated entry button) and ends by
 * submitting directly from 04E-03-JUSTIFICATIFS (no banking info, no mandate).
 */
export const APPEND_EXPENSES_FLOW: string[] = [
  '06-01-NOUVEAUFRAIS',       // Intro + free-text message
  '04E-00-REMBOURSEMENT',     // Reimbursement details (variant: no hasInvoices question)
  '04E-01-FACTURES',          // Invoice upload (variant: append banner + flow-specific navigation)
  '04E-02-VALIDATION',        // Expense validation (variant: shows previously submitted expenses)
  '04E-03-JUSTIFICATIFS'      // Supporting documents + final Submit (variant: different footer)
];

/**
 * Append Documents Flow (append-document)
 * Adds new supporting documents to an already-submitted claim (any flow type) when the
 * analyst has flagged it as INFORMATION_REQUESTED. Single-screen flow that bundles the
 * subtitle/instruction, optional 5000-char free-text message, upload zone and final submit
 * onto one page (06-00-NOUVEAUDOCUMENT).
 */
export const APPEND_DOCUMENTS_FLOW: string[] = [
  '06-00-NOUVEAUDOCUMENT'     // Intro + free-text message + upload + final Submit (single page)
];

/**
 * Get the next screen code in a flow
 *
 * @param currentScreen - Current screen code
 * @param flowType - Type of flow (MEDICAL, CANCELLATION, BAGGAGE)
 * @param skipConditions - Optional object with conditions to skip screens
 * @returns Next screen code or null if at end of flow
 */
export const getNextScreen = (
  currentScreen: string,
  flowType: FlowType,
  skipConditions?: Record<string, boolean>
): string | null => {
  const flow = getFlowByType(flowType);
  const currentIndex = flow.indexOf(currentScreen);

  if (currentIndex === -1 || currentIndex === flow.length - 1) {
    return null; // Screen not found or at end of flow
  }

  let nextIndex = currentIndex + 1;
  let nextScreen = flow[nextIndex];

  // Check if next screen should be skipped based on conditions
  if (skipConditions && nextScreen in CONDITIONAL_SCREENS) {
    const condition = CONDITIONAL_SCREENS[nextScreen].condition;
    if (skipConditions[condition] === false) {
      // Skip this screen and get the next one
      return getNextScreen(nextScreen, flowType, skipConditions);
    }
  }

  return nextScreen;
};

/**
 * Get the previous screen code in a flow
 *
 * @param currentScreen - Current screen code
 * @param flowType - Type of flow (MEDICAL, CANCELLATION, BAGGAGE)
 * @returns Previous screen code or null if at beginning of flow
 */
export const getPreviousScreen = (
  currentScreen: string,
  flowType: FlowType
): string | null => {
  const flow = getFlowByType(flowType);
  const currentIndex = flow.indexOf(currentScreen);

  if (currentIndex <= 0) {
    return null; // Screen not found or at beginning of flow
  }

  return flow[currentIndex - 1];
};

/**
 * Get flow progress percentage
 *
 * @param currentScreen - Current screen code
 * @param flowType - Type of flow (MEDICAL, CANCELLATION, BAGGAGE)
 * @returns Progress percentage (0-100)
 */
export const getFlowProgress = (
  currentScreen: string,
  flowType: FlowType
): number => {
  const flow = getFlowByType(flowType);
  const currentIndex = flow.indexOf(currentScreen);

  if (currentIndex === -1) {
    return 0;
  }

  return Math.round(((currentIndex + 1) / flow.length) * 100);
};

/**
 * Check if a screen is conditional in a specific flow
 */
export const isConditionalScreen = (screenCode: string, flowType: FlowType): boolean => {
  return screenCode in CONDITIONAL_SCREENS &&
    CONDITIONAL_SCREENS[screenCode].flows.includes(flowType);
};

/**
 * Get the flow type from a screen code
 * Returns the flow that the screen belongs to, or 'COMMON' if shared
 */
export const getFlowFromScreenCode = (screenCode: string): FlowType | 'COMMON' | null => {
  if (COMMON_SCREENS.includes(screenCode)) {
    return 'COMMON';
  }

  if (MEDICAL_FLOW.includes(screenCode)) {
    return 'MEDICAL';
  }

  if (CANCELLATION_FLOW.includes(screenCode)) {
    return 'CANCELLATION';
  }

  if (BAGGAGE_FLOW.includes(screenCode)) {
    return 'BAGGAGE';
  }

  return null;
};

/**
 * Validate if a screen transition is valid
 */
export const isValidTransition = (
  fromScreen: string,
  toScreen: string,
  flowType: FlowType
): boolean => {
  const flow = getFlowByType(flowType);
  const fromIndex = flow.indexOf(fromScreen);
  const toIndex = flow.indexOf(toScreen);

  if (fromIndex === -1 || toIndex === -1) {
    return false;
  }

  // Allow moving forward or backward within the flow
  return Math.abs(toIndex - fromIndex) === 1;
};
